import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PLAN_DEFINITIONS, TOPUP_CREDITS, TOPUP_PRICE_CENTS, SMS_TOPUP_CREDITS, SMS_TOPUP_PRICE_CENTS, type Plan } from '@wrapforce/shared/constants';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { useLocaleFormatters } from '@/lib/format';
import { useBillingDetail, useCheckout, useCreditLedger, usePortalSession, useSmsTopupCheckout, useTopupCheckout } from '@/features/billing/api';

const UPGRADE_PLANS: Plan[] = ['starter', 'pro', 'enterprise'];
const PLAN_RANK: Plan[] = ['trial', 'starter', 'pro', 'enterprise'];

export function BillingSettings({ workspaceId }: { workspaceId: string | null }) {
  const { t, i18n } = useTranslation('settings');
  const { formatMoney, formatDate, formatDateTime } = useLocaleFormatters();
  const { data: billing } = useBillingDetail(workspaceId);
  const { data: ledger } = useCreditLedger(workspaceId);
  const checkout = useCheckout(workspaceId);
  const portal = usePortalSession(workspaceId);
  const topup = useTopupCheckout(workspaceId);
  const smsTopup = useSmsTopupCheckout(workspaceId);
  const [interval, setInterval] = useState<'month' | 'year'>('month');

  async function handleUpgrade(plan: Plan) {
    const result = await checkout.mutateAsync({ plan: plan as 'starter' | 'pro' | 'enterprise', interval });
    window.location.assign(result.url);
  }
  async function handlePortal() {
    const result = await portal.mutateAsync();
    window.location.assign(result.url);
  }
  async function handleTopup() {
    const result = await topup.mutateAsync();
    window.location.assign(result.url);
  }
  async function handleSmsTopup() {
    const result = await smsTopup.mutateAsync();
    window.location.assign(result.url);
  }

  if (!billing) return null;
  const maxCredits = billing.planCreditsPerMonth + billing.topupCreditsRemaining;
  const maxSmsCredits = billing.smsCreditsPerMonth + billing.smsTopupCreditsRemaining;
  const smsMessagingEnabled = PLAN_DEFINITIONS[billing.plan].gates.smsMessaging;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>{t('billing.currentPlanTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="font-display text-lg font-bold text-ink">{t(`plan.${billing.plan}.label`)}</div>
              <div className="text-xs text-muted">
                {t('billing.seats', { count: billing.seatCount, limit: billing.seatLimit ?? '∞' })}
                {billing.subscriptionStatus !== 'canceled' && billing.currentPeriodEnd ? t('billing.renews', { date: formatDate(new Date(billing.currentPeriodEnd), 'MMM d, yyyy') }) : ''}
                {billing.subscriptionStatus !== 'canceled' && billing.cancelAtPeriodEnd ? t('billing.cancelsAtPeriodEnd') : ''}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" loading={topup.isPending} onClick={handleTopup}>
                {t('billing.buyCredits', { credits: TOPUP_CREDITS, price: formatMoney(TOPUP_PRICE_CENTS) })}
              </Button>
              {smsMessagingEnabled ? (
                <Button size="sm" variant="outline" loading={smsTopup.isPending} onClick={handleSmsTopup}>
                  {t('billing.buyTexts', { credits: SMS_TOPUP_CREDITS, price: formatMoney(SMS_TOPUP_PRICE_CENTS) })}
                </Button>
              ) : null}
              {billing.plan !== 'trial' ? (
                <Button size="sm" variant="outline" loading={portal.isPending} onClick={handlePortal}>
                  {t('billing.manageBilling')}
                </Button>
              ) : null}
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <div className="flex justify-between text-xs text-muted">
              <span>{t('billing.aiCredits')}</span>
              <span>
                {billing.aiCreditsBalance} / {maxCredits || billing.aiCreditsBalance}
              </span>
            </div>
            <div className="h-2 overflow-hidden bg-surface-2">
              <div className="h-full bg-accent" style={{ width: `${maxCredits > 0 ? Math.min(100, (billing.aiCreditsBalance / maxCredits) * 100) : 100}%` }} />
            </div>
          </div>
          {smsMessagingEnabled ? (
            <div className="flex flex-col gap-1">
              <div className="flex justify-between text-xs text-muted">
                <span>{t('billing.quoteTexts')}</span>
                <span>
                  {billing.smsCreditsBalance} / {maxSmsCredits || billing.smsCreditsBalance}
                </span>
              </div>
              <div className="h-2 overflow-hidden bg-surface-2">
                <div className="h-full bg-accent" style={{ width: `${maxSmsCredits > 0 ? Math.min(100, (billing.smsCreditsBalance / maxSmsCredits) * 100) : 100}%` }} />
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('billing.plansTitle')}</CardTitle>
          <div className="ml-auto flex gap-1 border border-border p-0.5 text-xs font-semibold">
            <button onClick={() => setInterval('month')} className={cn(' px-2.5 py-1', interval === 'month' ? 'bg-ink text-surface' : 'text-muted')}>
              {t('billing.monthly')}
            </button>
            <button onClick={() => setInterval('year')} className={cn(' px-2.5 py-1', interval === 'year' ? 'bg-ink text-surface' : 'text-muted')}>
              {t('billing.yearly')}
            </button>
          </div>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {UPGRADE_PLANS.map((plan) => {
            const def = PLAN_DEFINITIONS[plan];
            const priceCents = interval === 'month' ? def.priceMonthlyCents : def.priceYearlyCents;
            const isCurrent = billing.plan === plan;
            const features = t(`plan.${plan}.features`, { returnObjects: true }) as string[];
            const isDowngrade = billing.plan !== 'trial' && PLAN_RANK.indexOf(plan) < PLAN_RANK.indexOf(billing.plan);
            return (
              <div key={plan} className={cn('flex flex-col gap-2 rounded-[var(--radius-card)] border p-3.5', def.mostPopular ? 'border-accent' : 'border-border')}>
                <div className="h-4">
                  {def.mostPopular ? <span className="w-fit bg-accent px-2 py-0.5 text-[10px] font-bold uppercase text-on-accent">{t('billing.mostPopular')}</span> : null}
                </div>
                <div className="font-display text-base font-bold text-ink">{t(`plan.${plan}.label`)}</div>
                <div className="font-mono text-xl font-bold">{priceCents !== null ? formatMoney(priceCents) : '-'}<span className="text-xs font-normal text-muted">{interval === 'month' ? t('billing.perMonth') : t('billing.perYear')}</span></div>
                <ul className="flex flex-1 flex-col gap-1 text-xs text-muted">
                  {features.map((f, i) => (
                    <li key={i}>• {f}</li>
                  ))}
                </ul>
                <Button size="sm" className="mt-auto" variant={isCurrent ? 'outline' : 'primary'} disabled={isCurrent} loading={checkout.isPending} onClick={() => handleUpgrade(plan)}>
                  {isCurrent ? t('billing.currentPlanButton') : isDowngrade ? t('billing.changePlan') : t('billing.upgrade')}
                </Button>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('billing.creditLedgerTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-0 p-0">
          {(ledger?.items ?? []).map((entry) => {
            const reasonKey = `billing.creditReasons.${entry.reason}`;
            const reasonLabel = i18n.exists(`settings:${reasonKey}`) ? t(reasonKey) : entry.reason.replace('_', ' ');
            return (
            <div key={entry.id} className="flex items-center justify-between border-b border-border px-4 py-2.5 text-sm last:border-b-0">
              <div>
                <div className="font-medium capitalize text-ink">{reasonLabel}</div>
                <div className="text-xs text-muted">{formatDateTime(new Date(entry.createdAt))}</div>
              </div>
              <span className={cn('font-mono font-semibold', entry.deltaCredits > 0 ? 'text-success' : 'text-ink')}>
                {entry.deltaCredits > 0 ? '+' : ''}
                {entry.deltaCredits}
              </span>
            </div>
            );
          })}
          {(ledger?.items ?? []).length === 0 ? <p className="p-4 text-sm text-muted">{t('billing.noCreditActivity')}</p> : null}
        </CardContent>
      </Card>
    </div>
  );
}
